import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Button, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, LinearProgress, Tooltip, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { UPLOAD_TYPE } from '../apiServices';
import { isRecentActivity, UNFINISHED_FILE_STATUSES } from '../liveUploads';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import FolderIcon from '@material-ui/icons/Folder';
import FolderOpenIcon from '@material-ui/icons/FolderOpen';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import CloseIcon from '@material-ui/icons/Close';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import CreateNewFolderIcon from '@material-ui/icons/CreateNewFolder';
import NoteAddIcon from '@material-ui/icons/NoteAdd';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';

// Files rendered per expanded folder before "Show more", keeps 10k-file folders responsive
const FOLDER_PAGE_SIZE = 100;

// OS metadata files that should never be uploaded
const IGNORED_FILES = ['.DS_Store', 'Thumbs.db', 'desktop.ini'];
// macOS resource forks ("._report.pdf", left on non-Mac drives and zips) and Office lock files
// ("~$report.docx", present while a document is open)
const IGNORED_PREFIXES = ['._', '~$'];


export const formatBytes = (bytes) => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${units[i]}`;
};

const isIgnored = (name) => IGNORED_FILES.includes(name) || IGNORED_PREFIXES.some(prefix => name.startsWith(prefix));

// readEntries returns at most ~100 entries per call, so keep reading until it returns empty
const readAllEntries = (reader) => new Promise((resolve, reject) => {
  const entries = [];
  const readBatch = () => reader.readEntries((batch) => {
    if (!batch.length) {
      resolve(entries);
    } else {
      entries.push(...batch);
      readBatch();
    }
  }, reject);
  readBatch();
});

const collectEntryFiles = async (entry, out) => {
  if (entry.isFile) {
    const file = await new Promise((resolve, reject) => entry.file(resolve, reject));
    if (!isIgnored(file.name)) {
      out.push({ file, relativePath: entry.fullPath.replace(/^\//, '') });
    }
  } else if (entry.isDirectory) {
    const children = await readAllEntries(entry.createReader());
    for (const child of children) {
      await collectEntryFiles(child, out);
    }
  }
};

const fromFileList = (fileList) => Array.from(fileList)
  .filter(file => !isIgnored(file.name))
  .map(file => ({ file, relativePath: file.webkitRelativePath || file.name }));

// Paths shown per problem list before collapsing the rest into "and N more"
const RESUME_PROBLEM_PREVIEW = 5;
const DONE_FILE_STATUSES = ['COMPLETED', 'UPLOADED'];

// The server accepts any path and size on register, so the selection is checked here against the
// session's file list: unknown, extra or missing paths block the resume. Finished files are skipped
// whatever they are now. The server only checks part sizes, so a large file that already has parts
// uploaded is restarted rather than continued: a different file of the same size can't be told apart
// from the original, and continuing would mix the two. Single-PUT files are replaced whole, so they
// only need a restart when their size changed.
const getResumeCheck = (resumeSession, sessionFiles, items) => {
  if (!resumeSession || !items.length || !sessionFiles) return null;
  const expectedFiles = Number(resumeSession.total_files) || 0;
  const byPath = new Map(sessionFiles.map(file => [file.path, file]));
  const selectedPaths = new Set();
  const changed = [];
  const partial = [];
  const unregistered = [];
  let done = 0;
  items.forEach(({ file, relativePath }) => {
    selectedPaths.add(relativePath);
    const remote = byPath.get(relativePath);
    if (!remote) {
      unregistered.push(relativePath);
    } else if (DONE_FILE_STATUSES.includes(remote.status)) {
      done += 1;
    } else if (file.size !== remote.size) {
      changed.push(relativePath);
    } else if (remote.uploadType === UPLOAD_TYPE.MULTIPART && (remote.uploadedBytes > 0 || remote.status !== 'PENDING')) {
      partial.push(relativePath);
    }
  });
  // An upload interrupted while registering has files the server hasn't seen yet; only that many
  // unknown paths can be genuine, anything beyond is a file that isn't part of the upload
  const unregisteredAllowed = Math.max(0, expectedFiles - sessionFiles.length);
  const extra = unregistered.length > unregisteredAllowed ? unregistered : [];
  const missing = sessionFiles.filter(file => !selectedPaths.has(file.path)).map(file => file.path);
  const countMatches = items.length === expectedFiles;
  // Unfinished files touched in the last 2 minutes mean another device or user is still uploading;
  // resuming now would upload the same files twice
  const activeElsewhere = sessionFiles.filter(file => UNFINISHED_FILE_STATUSES.includes(file.status) && isRecentActivity(file.updatedAt)).map(file => file.path);
  return {
    blocked: !countMatches || !!extra.length || !!missing.length || !!activeElsewhere.length,
    countMatches, expectedFiles, extra, missing, changed, partial, activeElsewhere,
    restart: [...changed, ...partial],
    done, toUpload: items.length - done,
  };
};

const ProblemList = ({ title, paths }) => paths && paths.length ? (
  <div className="stream-upload-modal__problem-list">
    <b>{title} ({paths.length.toLocaleString()})</b>
    {paths.slice(0, RESUME_PROBLEM_PREVIEW).map(path => <div key={path} className="stream-upload-modal__problem-path">{path}</div>)}
    {paths.length > RESUME_PROBLEM_PREVIEW ? <div>and {(paths.length - RESUME_PROBLEM_PREVIEW).toLocaleString()} more</div> : null}
  </div>
) : null;

const StreamUploadModal = ({ dataRoom, open, onClose, onStartUpload, starting = false, resumeSession = null, resumeFiles = null, onRetryResumeFiles }) => {
  const [items, setItems] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [visibleCounts, setVisibleCounts] = useState({});
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);

  // React 17 doesn't forward webkitdirectory, so set it on the DOM node. A callback ref is needed because
  // the Dialog mounts its content in a portal after the first render, so a mount effect would see a null ref.
  const setFolderInputRef = useCallback((node) => {
    folderInputRef.current = node;
    if (node) {
      node.setAttribute('webkitdirectory', '');
      node.setAttribute('directory', '');
    }
  }, []);

  const addItems = (newItems) => {
    setItems(prevItems => {
      // Same relative path selected twice replaces the earlier one
      const byPath = new Map(prevItems.map(item => [item.relativePath, item]));
      newItems.forEach(item => byPath.set(item.relativePath, item));
      return Array.from(byPath.values());
    });
  };

  const onInputChange = (evt) => {
    addItems(fromFileList(evt.target.files));
    evt.target.value = ''; // allow re-selecting the same folder
  };

  const onDrop = async (evt) => {
    evt.preventDefault();
    setDragActive(false);
    // Entries must be read synchronously before the first await, the DataTransfer is cleared afterwards
    const entries = Array.from(evt.dataTransfer.items || [])
      .filter(item => item.kind === 'file')
      .map(item => item.webkitGetAsEntry ? item.webkitGetAsEntry() : null);
    if (!entries.length || entries.some(entry => !entry)) {
      addItems(fromFileList(evt.dataTransfer.files));
      return;
    }
    setScanning(true);
    try {
      const collected = [];
      for (const entry of entries) {
        await collectEntryFiles(entry, collected);
      }
      addItems(collected);
    } finally {
      setScanning(false);
    }
  };

  // Group by top-level folder/file so 10k files render as a handful of rows
  const groups = useMemo(() => {
    const map = new Map();
    items.forEach(({ file, relativePath }) => {
      const parts = relativePath.split('/');
      const isFolder = parts.length > 1;
      const name = parts[0];
      const key = `${isFolder ? 'd' : 'f'}:${name}`;
      const group = map.get(key) || { key, name, isFolder, count: 0, size: 0, files: [] };
      group.count += 1;
      group.size += file.size;
      group.files.push({ file, relativePath, subPath: parts.slice(1).join('/') });
      map.set(key, group);
    });
    const sorted = Array.from(map.values()).sort((a, b) => (b.isFolder - a.isFolder) || a.name.localeCompare(b.name));
    sorted.forEach(group => group.files.sort((a, b) => a.relativePath.localeCompare(b.relativePath)));
    return sorted;
  }, [items]);

  const folderCount = groups.filter(group => group.isFolder).length;

  const totalSize = useMemo(() => items.reduce((sum, item) => sum + item.file.size, 0), [items]);

  const removeGroup = (group) => {
    setItems(prevItems => prevItems.filter(({ relativePath }) => {
      const parts = relativePath.split('/');
      return !(parts[0] === group.name && (parts.length > 1) === group.isFolder);
    }));
  };

  const removeFile = (relativePath) => {
    setItems(prevItems => prevItems.filter(item => item.relativePath !== relativePath));
  };

  const clearAll = () => {
    setItems([]);
    setExpanded({});
    setVisibleCounts({});
  };

  const handleClose = () => {
    clearAll();
    onClose();
  };

  const folderName = dataRoom && dataRoom.navigation && dataRoom.navigation.length ? dataRoom.navigation[dataRoom.navigation.length - 1].name : 'loading...';
  const checkingResume = !!(resumeSession && (!resumeFiles || resumeFiles.loading));
  const resumeFilesFailed = !!(resumeSession && resumeFiles && !resumeFiles.loading && !resumeFiles.files);
  const resumeCheck = resumeSession && !checkingResume && !resumeFilesFailed ? getResumeCheck(resumeSession, resumeFiles.files, items) : null;
  const restartPaths = new Set(resumeCheck ? resumeCheck.restart : []);
  const startUpload = () => onStartUpload(restartPaths.size ? items.map(item => (restartPaths.has(item.relativePath) ? { ...item, restart: true } : item)) : items);

  return (
    <Dialog className="stream-upload-modal" open={open} onClose={scanning || starting ? undefined : handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {resumeSession ?
          <>Resume Upload{resumeSession.asset_name ? <> To "<b>{resumeSession.asset_name}</b>"</> : null}</> :
          <>Stream Upload To "<b>{folderName}</b>"</>
        }
      </DialogTitle>
      <DialogContent dividers>
        {resumeSession ?
          <Alert severity="info" className="stream-upload-modal__notice">
            Select the same files or folder you originally uploaded: <b>{(Number(resumeSession.total_files) || 0).toLocaleString()} files ({formatBytes(Number(resumeSession.total_bytes) || 0)})</b>. Files that already finished are skipped.
          </Alert> : null
        }
        {resumeFilesFailed ?
          <Alert
            severity="error"
            className="stream-upload-modal__notice"
            action={onRetryResumeFiles ? <Button color="inherit" size="small" onClick={onRetryResumeFiles}>Try Again</Button> : null}
          >
            Couldn't load this upload's file list, so the selection can't be checked yet.
          </Alert> : null
        }
        <div
          className={`stream-upload-modal__drop-area${items.length ? ' stream-upload-modal__drop-area--compact' : ''}${dragActive ? ' stream-upload-modal__drop-area--active' : ''}`}
          onDragOver={(evt) => { evt.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={onDrop}
        >
          {items.length ? (
            <Typography variant="body2" color="textSecondary">
              <CloudUploadIcon className="stream-upload-modal__upload-icon-compact" />Drop more files or folders here
            </Typography>
          ) : (
            <>
              <CloudUploadIcon className="stream-upload-modal__upload-icon" />
              <Typography variant="body1">Drag & drop files or folders here</Typography>
              <Typography variant="caption" color="textSecondary">or</Typography>
            </>
          )}
          <div className={items.length ? 'stream-upload-modal__pick-buttons stream-upload-modal__pick-buttons--compact' : 'stream-upload-modal__pick-buttons'}>
            <Button disabled={scanning} onClick={() => fileInputRef.current.click()} variant="outlined" color="primary" size="small" startIcon={<NoteAddIcon />}>Select Files</Button>
            <Button disabled={scanning} onClick={() => folderInputRef.current.click()} variant="outlined" color="primary" size="small" startIcon={<CreateNewFolderIcon />}>Select Folder</Button>
          </div>
          <input ref={fileInputRef} type="file" multiple hidden onChange={onInputChange} />
          <input ref={setFolderInputRef} type="file" multiple hidden onChange={onInputChange} />
        </div>
        {scanning ? (
          <div className="stream-upload-modal__scanning">
            <Typography variant="caption" color="textSecondary">Reading folder contents...</Typography>
            <LinearProgress />
          </div>
        ) : null}
        {items.length ? (
          <>
            <div className="stream-upload-modal__summary">
              <Typography variant="subtitle2">
                {folderCount ? `${folderCount.toLocaleString()} folder${folderCount > 1 ? 's' : ''} · ` : ''}
                {items.length.toLocaleString()} file{items.length > 1 ? 's' : ''} selected ({formatBytes(totalSize)})
              </Typography>
              <Button size="small" onClick={clearAll}>Clear All</Button>
            </div>
            <div className="stream-upload-modal__list">
              {groups.map(group => {
                if (!group.isFolder) {
                  const { relativePath, file } = group.files[0];
                  return (
                    <div key={group.key} className="stream-upload-modal__group-row stream-upload-modal__group-row--file">
                      <InsertDriveFileIcon color="primary" fontSize="small" className="stream-upload-modal__group-icon" />
                      <Typography variant="body2" className="stream-upload-modal__group-name" title={relativePath}>{group.name}</Typography>
                      <span className="stream-upload-modal__meta">{formatBytes(file.size)}</span>
                      <Tooltip title="Remove" arrow>
                        <IconButton size="small" onClick={() => removeFile(relativePath)}><CloseIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </div>
                  );
                }
                // A lone folder opens by default, there is nothing else to scan past
                const isOpen = expanded[group.key] !== undefined ? expanded[group.key] : groups.length === 1;
                const visible = visibleCounts[group.key] || FOLDER_PAGE_SIZE;
                const remaining = group.files.length - visible;
                return (
                  <div key={group.key}>
                    <div
                      role="button"
                      tabIndex={0}
                      aria-expanded={isOpen}
                      className={`stream-upload-modal__group-row${isOpen ? ' stream-upload-modal__group-row--open' : ''}`}
                      onClick={() => setExpanded(prev => ({ ...prev, [group.key]: !isOpen }))}
                      onKeyDown={(evt) => { if (evt.key === 'Enter' || evt.key === ' ') { evt.preventDefault(); setExpanded(prev => ({ ...prev, [group.key]: !isOpen })); } }}
                    >
                      <ChevronRightIcon className={`stream-upload-modal__chevron${isOpen ? ' stream-upload-modal__chevron--open' : ''}`} />
                      {isOpen ? <FolderOpenIcon color="primary" fontSize="small" className="stream-upload-modal__group-icon" /> : <FolderIcon color="primary" fontSize="small" className="stream-upload-modal__group-icon" />}
                      <Typography variant="body2" className="stream-upload-modal__group-name" title={group.name}>{group.name}</Typography>
                      <span className="stream-upload-modal__meta">{group.count.toLocaleString()} file{group.count > 1 ? 's' : ''} · {formatBytes(group.size)}</span>
                      <Tooltip title="Remove folder" arrow>
                        <IconButton size="small" onClick={(evt) => { evt.stopPropagation(); removeGroup(group); }}><DeleteOutlineIcon fontSize="small" color="error" /></IconButton>
                      </Tooltip>
                    </div>
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                      {group.files.slice(0, visible).map(({ file, relativePath, subPath }) => {
                        const slash = subPath.lastIndexOf('/');
                        return (
                          <div key={relativePath} className="stream-upload-modal__file-row">
                            <InsertDriveFileIcon className="stream-upload-modal__file-icon" />
                            <span className="stream-upload-modal__file-name" title={relativePath}>
                              {slash > -1 ? <span className="stream-upload-modal__file-dir">{subPath.slice(0, slash + 1)}</span> : null}
                              {file.name}
                            </span>
                            <span className="stream-upload-modal__meta">{formatBytes(file.size)}</span>
                            <Tooltip title="Remove" arrow>
                              <IconButton size="small" onClick={() => removeFile(relativePath)}><CloseIcon className="stream-upload-modal__remove-icon" /></IconButton>
                            </Tooltip>
                          </div>
                        );
                      })}
                      {remaining > 0 ? (
                        <div className="stream-upload-modal__show-more">
                          <Button size="small" color="primary" onClick={() => setVisibleCounts(prev => ({ ...prev, [group.key]: visible + FOLDER_PAGE_SIZE }))}>
                            Show {Math.min(FOLDER_PAGE_SIZE, remaining).toLocaleString()} more ({remaining.toLocaleString()} remaining)
                          </Button>
                        </div>
                      ) : null}
                    </Collapse>
                  </div>
                );
              })}
            </div>
          </>
        ) : null}
        {checkingResume && items.length ?
          <div className="stream-upload-modal__check">
            <Typography variant="caption" color="textSecondary">Checking the files already in this upload...</Typography>
            <LinearProgress />
          </div> : null
        }
        {resumeCheck && resumeCheck.blocked ?
          <Alert severity="error" className="stream-upload-modal__check">
            {resumeCheck.activeElsewhere.length && !resumeCheck.extra.length && !resumeCheck.missing.length && resumeCheck.countMatches ?
              'This upload was active in the last 2 minutes and may still be running on another device. Try again in a few minutes.' :
              'This selection doesn\'t match the interrupted upload. Clear it and choose the original files, without renaming, moving, adding or removing any.'}
            {!resumeCheck.countMatches ?
              <div className="stream-upload-modal__problem-list">
                Expected <b>{resumeCheck.expectedFiles.toLocaleString()} files</b>, selected <b>{items.length.toLocaleString()}</b>.
              </div> : null
            }
            <ProblemList title="Not part of this upload" paths={resumeCheck.extra} />
            <ProblemList title="Missing from your selection" paths={resumeCheck.missing} />
            <ProblemList title="Still being uploaded elsewhere" paths={resumeCheck.activeElsewhere} />
          </Alert> : null
        }
        {resumeCheck && !resumeCheck.blocked ?
          <Alert severity={resumeCheck.changed.length ? 'warning' : 'success'} className="stream-upload-modal__check">
            All files belong to this upload: <b>{resumeCheck.done.toLocaleString()}</b> already uploaded will be skipped, <b>{resumeCheck.toUpload.toLocaleString()}</b> will be uploaded.
            <ProblemList title="Changed since the upload started, will be uploaded again from the start" paths={resumeCheck.changed} />
            <ProblemList title="Large files that were partly uploaded, will start again" paths={resumeCheck.partial} />
          </Alert> : null
        }
      </DialogContent>
      <DialogActions>
        <Button color="primary" disabled={scanning || starting} onClick={handleClose}>Cancel</Button>
        <Button
          color="primary"
          variant="contained"
          disabled={scanning || starting || !items.length || checkingResume || !!(resumeCheck && resumeCheck.blocked)}
          onClick={startUpload}
        >
          {starting ? 'Starting...' : resumeSession ? 'Resume Upload' : 'Start Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StreamUploadModal;
